import { useEffect, useMemo, useRef, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import type { CompanyMethodSetupFieldsProps } from "./companyMethodContracts";

/** Company-only Elements boundary: explicit original key/secret, no wallet hook. */
export function CompanyMethodSetupFields(props: CompanyMethodSetupFieldsProps) {
  const stripe = useMemo(() => loadStripe(props.challenge.publishable_key), [props.challenge.publishable_key]);
  const options = useMemo(() => ({ clientSecret: props.challenge.client_secret }), [props.challenge.client_secret]);
  return <Elements key={props.challenge.operation_id} stripe={stripe} options={options}>
    <CompanyHostedFields {...props} />
  </Elements>;
}

function CompanyHostedFields({ challenge, onReconcile, onDismiss, onBusyChange }: CompanyMethodSetupFieldsProps) {
  const stripe = useStripe(), elements = useElements();
  const [complete, setComplete] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState<string | null>(null);
  const live = useRef(true), inFlight = useRef(false);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  async function confirm() {
    if (!stripe || !elements || inFlight.current) return;
    inFlight.current = true; setBusy(true); onBusyChange(true); setError(null);
    try {
      const submitted = await elements.submit();
      if (submitted.error) { if (live.current) setError("Check the hosted card fields before continuing."); return; }
      try { await stripe.confirmSetup({ elements, clientSecret: challenge.client_secret, redirect: "if_required" }); }
      catch { if (live.current) setError("Confirmation is unresolved. Check the original card setup status."); }
      if (live.current) await onReconcile(challenge.operation_id);
    } catch {
      if (live.current) {
        setError("Confirmation is unresolved. Check the original card setup status.");
      }
    } finally {
      inFlight.current = false;
      if (live.current) setBusy(false);
      onBusyChange(false);
    }
  }
  return <Stack spacing={1.5}>
    <PaymentElement onChange={(event) => setComplete(event.complete)} />
    {error ? <Alert severity="warning">{error}</Alert> : null}
    <Stack direction="row" spacing={1}>
      <Button size="small" variant="contained" disabled={!stripe || !elements || busy || (!complete && challenge.disposition !== "requires_action")}
        onClick={() => void confirm()}>{busy ? "Confirming…" : "Save company card"}</Button>
      <Button size="small" disabled={busy} onClick={onDismiss}>Dismiss</Button>
    </Stack>
  </Stack>;
}
